import { inject, Injectable, signal } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  DatatableStudentActivityHistory,
  Student,
  StudentDurationLesson,
} from "../model/student.model";
import {
  StudentExamResultParams,
  StudentSummaryResult,
} from "../model/student-exam-result.model";
import {
  ParentingInteraction,
  UnderParenting,
} from "../model/under-parenting.model";

@Injectable({ providedIn: "root" })
export class MonitorService {
  #http = inject(HttpClient);
  private excepted_key_names: string[] = [];
  public monitorStudentId = signal<number | null>(null);
  public monitorStudent = signal<Student | null>(null);
  private apiStudentUrl = "/students";
  _setFilter(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v != null)
    );
  }

  _setParams(value: any) {
    if (!value) return;
    const filter: any = this._setFilter(value);
    let params = new HttpParams();
    for (const key in filter) {
      if (Array.isArray(filter[key])) {
        params = params.append(key, JSON.stringify(filter[key]));
      } else {
        params = params.append(key, filter[key]);
      }
    }
    return params;
  }
  _setFormData(object_key: string, value: any) {
    let formData = new FormData();
    for (const key in value) {
      let obj = value[key];
      if (Array.isArray(obj)) {
        obj.forEach((e, i) => {
          if (this.excepted_key_names.includes(key)) {
            formData.append(`${object_key}[${key}][]`, e);
          } else {
            for (const key_i in e) {
              formData.append(`${object_key}[${key}][][${key_i}]`, e[key_i]);
            }
          }
        });
      } else {
        if (value[key]) {
          formData.append(`${object_key}[${key}]`, value[key]);
        }
      }
    }
    return formData;
  }

  //-------------------------------------------------------///

  /**
   * Whose record these calls are about.
   *
   * `monitorStudent` is set by the child picker on the monitor page. On a
   * direct load of a tab — a bookmark, a reload, the menu — that picker has not
   * resolved yet, but the child is right there in the URL. Reading the URL as
   * the fallback stops the first fetch going out unscoped and coming back
   * empty for a parent, who has no student record of their own.
   */
  private targetStudentId(): string {
    const fromPicker = this.monitorStudent()?.id;
    if (fromPicker) return String(fromPicker);

    const fromUrl = new URLSearchParams(window.location.search).get(
      "student_id"
    );
    return fromUrl ?? "";
  }

  getDurationLessons(params?: {
    back_date?: number;
  }): Observable<StudentDurationLesson[]> {
    const queryParams = this._setParams(params);
    return this.#http.get<StudentDurationLesson[]>(
      `${this.apiStudentUrl}/duration_lesson/${this.targetStudentId()}?${queryParams}`
    );
  }
  getDurationLesson(
    student_id?: number,
    params?: any
  ): Observable<StudentDurationLesson[]> {
    const queryParams = this._setParams(params);
    return this.#http.get<StudentDurationLesson[]>(
      `${this.apiStudentUrl}/duration_lesson/${student_id}?${queryParams}`
    );
  }

  getActivityHistory(
    params?: any
  ): Observable<DatatableStudentActivityHistory[]> {
    const queryParams = this._setParams(params);
    return this.#http.get<DatatableStudentActivityHistory[]>(
      `${this.apiStudentUrl}/activity_history/${this.targetStudentId()}?${queryParams}`
    );
  }

  getSummaryResult(
    params?: StudentExamResultParams
  ): Observable<StudentSummaryResult> {
    const queryParams = this._setParams(params);

    return this.#http.get<StudentSummaryResult>(
      `/summary_exam_results/${this.targetStudentId()}?${queryParams}`
    );
  }

  getSummaryResultById(
    student_id: number,
    params?: StudentExamResultParams
  ): Observable<StudentSummaryResult> {
    const queryParams = this._setParams(params);
    return this.#http.get<StudentSummaryResult>(
      `/summary_exam_results/${student_id}?${queryParams}`
    );
  }
  getStudentExamResult(
    params?: StudentExamResultParams
  ): Observable<StudentSummaryResult> {
    if (this.monitorStudent()) {
      params!.student_id = this.monitorStudent()?.id;
    }
    const queryParams = this._setParams(params);
    return this.#http.get<StudentSummaryResult>(
      `/student_exam_results?${queryParams}`
    );
  }

  //-------------------------------------------------------///

  getUnderParenting(): Observable<UnderParenting[]> {
    return this.#http.get<UnderParenting[]>("/under_parentings");
  }

  getCodeInvitation(): Observable<any> {
    return this.#http.get(`/parent_invitations`);
  }

  addStudentUnderParenting(payload: ParentingInteraction): Observable<any> {
    return this.#http.post(`/parenting_interactions`, {
      parenting_interaction: payload,
    });
  }

  unlinkStudent(
    payload: ParentingInteraction
  ): Observable<ParentingInteraction> {
    return this.#http.post<ParentingInteraction>(`/parenting_interactions`, {
      parenting_interaction: payload,
    });
  }
}
